import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SectionType } from '@amzn/handshake-painter-typescript-client/v1';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { useBundle } from '@amzn/react-arb-tools';
import styled from '@emotion/styled';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useAnalyticsContext } from 'src/common/metrics';
import { LoadingBar, SectionDivider } from 'src/common/styled';
import { useFetchRufusAnswer, useFetchRufusPills } from 'src/hooks';
import { RufusLoader } from './RufusLoader';
import { RufusPillCarousel } from './RufusPillCarousel';
import { RufusText } from './RufusText';
import { RufusWidgetHeader } from './RufusWidgetHeader';
var WidgetState;
(function (WidgetState) {
    WidgetState["INITIAL_WIDGET_LOADING"] = "INITIAL_WIDGET_LOADING";
    WidgetState["INITIAL_PILLS_FETCH_ERROR"] = "INITIAL_PILLS_FETCH_ERROR";
    WidgetState["SHOW_INITIAL_PILLS"] = "SHOW_INITIAL_PILLS";
    WidgetState["ANSWER_LOADING"] = "ANSWER_LOADING";
    WidgetState["ANSWER_ERROR"] = "ANSWER_ERROR";
    WidgetState["SHOW_ANSWER"] = "SHOW_ANSWER";
})(WidgetState || (WidgetState = {}));
const AnimatedWidgetContainer = styled.div `
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.4s ease-out;
`;
export const RufusWidget = ({ topSpacing, bottomSpacing, includeTopBorder, headerSize }) => {
    var _a;
    const [bundle, isBundleLoading] = useBundle('pages.productDetails');
    const [selectedQuery, setSelectedQuery] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const widgetRef = useRef(null);
    const hasScrolledRef = useRef(false);
    const hasClickedRef = useRef(false);
    const rumClient = useAnalyticsContext();
    const { asin } = useParams();
    const { data: pills, isLoading: isPillsLoading, isError: isPillsError } = useFetchRufusPills();
    const { data: answer, isLoading: isAnswerLoading, isError: isAnswerError, } = useFetchRufusAnswer({ query: selectedQuery });
    const widgetState = useMemo(() => {
        if (isBundleLoading || isPillsLoading)
            return WidgetState.INITIAL_WIDGET_LOADING;
        if (isPillsError || !(pills === null || pills === void 0 ? void 0 : pills.rufusPills) || pills.rufusPills.length === 0)
            return WidgetState.INITIAL_PILLS_FETCH_ERROR;
        if (!selectedQuery)
            return WidgetState.SHOW_INITIAL_PILLS;
        if (isAnswerLoading)
            return WidgetState.ANSWER_LOADING;
        if (isAnswerError || (answer === null || answer === void 0 ? void 0 : answer.rufusAnswerSections.some((section) => section.sectionType === SectionType.Error)))
            return WidgetState.ANSWER_ERROR;
        return WidgetState.SHOW_ANSWER;
    }, [isBundleLoading, isPillsLoading, isPillsError, selectedQuery, isAnswerLoading, isAnswerError, answer]);
    // Smooth fade in animation on initial widget display. Also emitting metrics to track health for launch.
    useEffect(() => {
        if (widgetState === WidgetState.SHOW_INITIAL_PILLS) {
            rumClient.recordEvent('RUFUS_INITIAL_PILLS_SHOWN', {
                url: window.location.href,
                asin,
            });
            requestAnimationFrame(() => {
                setIsVisible(true);
            });
        }
        else if (widgetState === WidgetState.ANSWER_ERROR) {
            rumClient.recordEvent('RUFUS_ANSWER_ERROR', {
                url: window.location.href,
                asin,
            });
        }
        else if (widgetState === WidgetState.SHOW_ANSWER) {
            rumClient.recordEvent('RUFUS_ANSWER_SHOWN', {
                url: window.location.href,
                asin,
            });
        }
    }, [widgetState, rumClient, asin]);
    const renderAnswerSections = () => {
        if (!(answer === null || answer === void 0 ? void 0 : answer.rufusAnswerSections))
            return null;
        return (_jsx(Column, { spacing: "300", children: answer.rufusAnswerSections.map((section, index) => {
                switch (section.sectionType) {
                    case SectionType.Text:
                        return _jsx(RufusText, { textSection: section.textSection }, index);
                    case SectionType.Carousel: {
                        const pills = section
                            .carouselSection.rufusCarouselEntries.filter((card) => { var _a; return (_a = card.pillEntry) === null || _a === void 0 ? void 0 : _a.questionText; })
                            .map((card) => ({ questionText: card.pillEntry.questionText }));
                        return (_jsx(RufusPillCarousel, { pills: pills, setSelectedQuery: setSelectedQuery, widgetRef: widgetRef, hasScrolledRef: hasScrolledRef, hasClickedRef: hasClickedRef }, index));
                    }
                    default:
                        return null;
                }
            }) }));
    };
    const initialPillCarousel = (_jsx(RufusPillCarousel, { pills: pills === null || pills === void 0 ? void 0 : pills.rufusPills, setSelectedQuery: setSelectedQuery, widgetRef: widgetRef, hasScrolledRef: hasScrolledRef, hasClickedRef: hasClickedRef }));
    if ([WidgetState.INITIAL_WIDGET_LOADING, WidgetState.INITIAL_PILLS_FETCH_ERROR].includes(widgetState))
        return null;
    return (_jsxs(AnimatedWidgetContainer, { isVisible: isVisible, children: [includeTopBorder && _jsx(SectionDivider, {}), _jsxs(Column, { spacingInset: `${topSpacing} 350 ${bottomSpacing} 350`, spacing: "300", ref: widgetRef, children: [_jsx(RufusWidgetHeader, { headerSize: headerSize }), selectedQuery && _jsx(Text, { type: "h50", children: selectedQuery }), widgetState === WidgetState.SHOW_INITIAL_PILLS && initialPillCarousel, widgetState === WidgetState.ANSWER_LOADING && (_jsxs(Column, { spacingInset: "none", spacing: "300", children: [_jsx(LoadingBar, {}), _jsx(LoadingBar, {}), _jsx(RufusLoader, {})] })), widgetState === WidgetState.ANSWER_ERROR && (_jsxs(_Fragment, { children: [_jsx(Text, { type: "b200", children: bundle.getMessage('cantHelpWithThat') }), initialPillCarousel] })), widgetState === WidgetState.SHOW_ANSWER && (_jsxs(_Fragment, { children: [renderAnswerSections(), !((_a = answer === null || answer === void 0 ? void 0 : answer.rufusAnswerSections) === null || _a === void 0 ? void 0 : _a.some((s) => s.sectionType === SectionType.Carousel)) && initialPillCarousel] }))] })] }));
};
